import {useFieldArray, useFormContext} from "react-hook-form";
import {BrandEditorImage, BrandEditorItem} from "./BrandItemEditor";
import {
   Box,
   Card,
   CardActions,
   CardContent,
   Chip,
   Dialog,
   DialogActions,
   DialogContent,
   Divider,
   IconButton,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Tooltip,
   Typography,
   useMediaQuery,
   useTheme
} from "@mui/material";
import {Thumbnail} from "../../../components/Thumbnail";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MenuIcon from "@mui/icons-material/Menu";
import {
   EnumItemIssueLocation,
   ItemIssueFragment,
   SortFindManyItemIssueInput,
   useItemEditorImage2LazyQuery,
   useItemEditorImageLazyQuery,
   useItemIssuesQuery
} from "../../../Queries";
import {CSSProperties, useEffect, useState} from "react";
import {CheckBox, CheckBoxOutlineBlank, Edit, Visibility, VisibilityOff} from "@mui/icons-material";
import {DateFormat} from "../../../utility/DateFormat";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import {BrandItemImageEditor} from "./BrandItemImageEditor";
import {UploadImage} from "../../../components/UploadImage";
import {createUploadService} from "../../../UploadService";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {BrandItemImportDialog} from "./BrandItemImportDialog";
import {ItemImageStatus} from "./ItemImageStatus";
import {BrandProductImage} from "./BrandProductImage";
import {CloseIcon} from "../../../components/Icons";

type BrandItemImagesProps = {
   brandHouseId: string
   brandItemId?: string
}

export const BrandItemImages = ({brandHouseId, brandItemId}: BrandItemImagesProps) => {
   const {control, setValue} = useFormContext<BrandEditorItem>();
   const [importing, setImporting] = useState<boolean>(false);
   const [editAltText, setEditAltText] = useState<number | undefined>(undefined);

   const {fields: images, append, remove, move, update} = useFieldArray({
      control,
      name: "images",
   });

   // Issue photos are read-only documentation of defects, fetched independently of the
   // editable image field array. They are shown as clearly-marked cards within this section.
   const {data: issuesData} = useItemIssuesQuery({
      skip: !brandItemId,
      fetchPolicy: "no-cache",
      variables: {
         perPage: 100,
         sort: SortFindManyItemIssueInput._ID_DESC,
         filter: {brandHouseId, brandItemId},
      },
   });
   const issues = (issuesData?.ItemIssues?.items || []).filter((issue): issue is ItemIssueFragment => !!issue);

   const onUploadImages = (imageIds: string[] | string) => {
      for (const imageId of imageIds) {
         append({
            uploadId: imageId,
            process: false,
            originalImage: false
         })
      }
   }

   const onImport = () => {
      setImporting(true);
   }

   const uploadService = createUploadService(import.meta.env.VITE_FILESERVICEURL!, brandHouseId)

   return <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
      {images.map((image, index) => {
         return <Box key={image._id || 'i_' + index}>
            <ItemImage
               brandHouseId={brandHouseId}
               image={image}
               onDelete={() => remove(index)}
               onMoveUp={() => move(index, index - 1)}
               onMoveDown={() => move(index, index + 1)}
               onUpdate={(newImageId: string) => {
                  update(index, {
                     ...image,
                     uploadId: newImageId,
                     imageId: null
                  } as any);
               }}
               onToggleBackground={() => {
                  update(index, {
                     ...image,
                     process: !image.process,
                     imageId: null
                  } as any);
               }}
               onToggleOriginal={() => {
                  update(index, {
                     ...image,
                     originalImage: !image.originalImage,
                     process: true
                  } as any);
               }}
               onUpdateImageId={(imageId) => {
                  setValue(`images.${index}.imageId`, imageId, {
                     shouldTouch: true,
                     shouldDirty: false
                  });
               }}
            />
         </Box>
      })}
      {issues.map((issue) => <IssueImageCard key={issue._id} issue={issue}/>)}
      <UploadImage id={"brandItemImageUpload"}
                   size={220}
                   uploadService={uploadService}
                   addImages={onUploadImages}>
         <Button variant={"outlined"} onClick={onImport} style={{marginLeft: '10px'}}
                 disabled={!brandItemId}>Import</Button>
      </UploadImage>
      <Dialog open={editAltText !== undefined} onClose={() => setEditAltText(undefined)}>
         <DialogContent>
            <FormInputText name={`images[${editAltText}].altText`} label={"Image alt text"}/>
         </DialogContent>
         <DialogActions>
            <Button variant={"contained"} onClick={() => setEditAltText(undefined)}>Update</Button>
         </DialogActions>
      </Dialog>
      {(importing && brandItemId) &&
         <BrandItemImportDialog brandItemId={brandItemId}
                                onSelect={img => {
                                   // Mirror Core's adjustAutoImages flow: append source-independently.
                                   // Use imageId when present (a storage image id), otherwise let Core
                                   // fetch the image from its url. Never map imageId to brandProductImageId.
                                   append({
                                      ...(img.imageId
                                         ? {imageId: img.imageId}
                                         : {uploadUrl: img.imageUrl}),
                                      originalImage: true,
                                      perspective: img.perspective ?? undefined,
                                      type: img.type ?? undefined,
                                      altText: img.altText ?? undefined,
                                      externId: img.externId ?? undefined,
                                   });
                                }}
                                onHide={() => setImporting(false)}/>
      }
   </Box>
}

type BrandItemImageProps = {
   brandHouseId: string
   image: BrandEditorImage
   onDelete: () => void
   onMoveUp: () => void
   onMoveDown: () => void
   onUpdate: (newImageId: string) => void
   onToggleBackground: () => void
   onToggleOriginal: () => void
   onUpdateImageId: (imageId: string) => void
}

type ImageStorage = {
   imageUrl?: string
   uploadUrl?: string
}

export const ItemImage = ({
                             brandHouseId,
                             image,
                             onUpdate,
                             onMoveUp,
                             onMoveDown,
                             onDelete,
                             onToggleBackground,
                             onToggleOriginal,
                             onUpdateImageId
                          }: BrandItemImageProps) => {
   const [imageStorage, setImageStorage] = useState<ImageStorage>({});
   const [showOther, setShowOther] = useState<boolean>(false);
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [showMenu, setShowMenu] = useState<boolean>(false);
   const [editImageId, setEditImageId] = useState<string | undefined>(undefined);

   const [fetchUrl] = useItemEditorImageLazyQuery();
   const [fetchUrl2] = useItemEditorImage2LazyQuery();

   useEffect(() => {
      if (image.imageId || image.uploadId) {
         (async function () {
            let imgUrl = '';
            let uplUrl = '';

            if (image.imageId) {
               const {data: imageData} = await fetchUrl2({
                  variables: {
                     imageId: image.imageId
                  }
               });
               imgUrl = imageData?.StorageFileById?.url || '';
            }

            if (image.uploadId) {
               const {data: imageData} = await fetchUrl({
                  variables: {
                     imageId: image.uploadId
                  }
               });
               uplUrl = imageData?.Image?.url || '';
            }

            setImageStorage({
               imageUrl: imgUrl,
               uploadUrl: uplUrl
            });
         })();
      }
   }, [fetchUrl, fetchUrl2, image.imageId, image.uploadId]);

   const open = Boolean(anchorEl);
   const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setShowMenu(true);
      setAnchorEl(event.currentTarget);
   };

   const onUpdateImage = async (newImageId: string) => {
      onUpdate(newImageId);
      setImageStorage({}); // Reset image storage to trigger a re-fetch
      setEditImageId(undefined);
   };

   return (
      <Box>
         <Card sx={{ width: 245, marginBottom: '15px', marginLeft: '7px', marginRight: '7px', position: 'relative', borderColor: '#f0f0f0' }} variant={"outlined"}>
            <CardContent sx={{ padding: '4px 8px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '28px' }}>
               <CardTypeLabel label={(image.brandProductImageId || image.originalImage) ? 'Original image' : 'Packshot'} />
               {imageStorage.imageUrl && imageStorage.uploadUrl &&
                  <Tooltip title={showOther ? 'View processed image' : 'View original image'}>
                     <IconButton size="small" onClick={() => setShowOther(!showOther)}>
                        {showOther ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                     </IconButton>
                  </Tooltip>}
            </CardContent>
            <CardContent sx={{ position: 'relative' }}>
               {image.brandProductImageId && <BrandProductImage brandProductImageId={image.brandProductImageId} />}
               {!image.brandProductImageId && <>
                  <Thumbnail image={ (imageStorage.imageUrl && !showOther) ? imageStorage.imageUrl : (imageStorage.uploadUrl || image.uploadUrl) } size={200} hideBorder={true} />
                  {image._id && <ItemImageStatus sx={{ position: 'absolute', bottom: '5px', left: '5px', right: '5px', }} imageId={image._id} process={image.process || false} onUpdateImageId={onUpdateImageId} />}
               </>}
            </CardContent>
            <CardActions>
               <Grid container>
                  <Grid item xs={12} container justifyContent={"space-between"}>
                     <Button variant={"outlined"} size="small" color="secondary" onClick={handleClick}><MenuIcon /></Button>
                     {!image.brandProductImageId && <Button variant={"outlined"} size="small" color="secondary" onClick={() => setEditImageId(image.uploadId)}>
                        <Edit />
                     </Button>}

                     <Button className={"tradein-blackbutton"} variant={"outlined"} size="small" color="secondary" onClick={onDelete}>
                        <DeleteForeverIcon />
                     </Button>
                  </Grid>
               </Grid>
            </CardActions>
         </Card>
         {showMenu &&
            <Menu
               id={"long-menu"}
               MenuListProps={{ 'aria-labelledby': 'long-button' }}
               anchorEl={anchorEl}
               open={open}
               onClose={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAnchorEl(null);
               }}
            >
               {!image.brandProductImageId && [
                  <MenuItem onClick={onToggleBackground}>
                     <ListItemIcon>
                        {image.process ? <CheckBox fontSize="small" /> : <CheckBoxOutlineBlank fontSize={"small"} />}
                     </ListItemIcon>
                     <ListItemText>Remove background</ListItemText>
                  </MenuItem>,
                  <Divider />
               ]}
               {image.uploadId && [
                  <MenuItem onClick={onToggleOriginal}>
                     <ListItemIcon>
                        {image.originalImage ? <CheckBox fontSize="small" /> : <CheckBoxOutlineBlank fontSize={"small"} />}
                     </ListItemIcon>
                     <ListItemText>Original product image</ListItemText>
                  </MenuItem>,
                  <Divider />
               ]}
               <MenuItem onClick={onMoveUp}>
                  <ListItemIcon><MoveUpIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Move up</ListItemText>
               </MenuItem>
               <MenuItem onClick={onMoveDown}>
                  <ListItemIcon><MoveDownIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Move down</ListItemText>
               </MenuItem>
            </Menu>
         }

         {editImageId &&
            <BrandItemImageEditor imageId={editImageId} brandHouseId={brandHouseId} show={!!editImageId} onClose={() => setEditImageId(undefined)} onUpdate={onUpdateImage} />
         }
      </Box>
   );
};

const ISSUE_IMAGE_SIZE = 200;

type CardTypeLabelProps = {
   label: string
   color?: string
}

// Small uppercase caption identifying what kind of image a card shows.
const CardTypeLabel = ({label, color = '#808080'}: CardTypeLabelProps) => {
   return <Typography variant={"caption"}
                      sx={{fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color}}>
      {label}
   </Typography>;
};

type IssueImageCardProps = {
   issue: ItemIssueFragment
}

type IssueImageProps = {
   issue: ItemIssueFragment
   imgStyle: CSSProperties
   markSize: number
   onClick?: () => void
}

// Issue photo with its defect marker overlaid. Marker coordinates (markX / markY) are
// percentages of the image width / height, so the same markup renders correctly both as a
// card thumbnail and full screen - the wrapper shrinks to the image's rendered size.
const IssueImage = ({issue, imgStyle, markSize, onClick}: IssueImageProps) => {
   const imageUrl = issue.imageUrl || issue.processedUrl || undefined;
   const hasMark = issue.markX !== null && issue.markX !== undefined && issue.markY !== null && issue.markY !== undefined;

   return <Box onClick={onClick}
               sx={{position: 'relative', display: 'inline-block', cursor: onClick ? 'pointer' : 'default'}}>
      <img src={imageUrl}
           alt={issue.issue || 'Issue'}
           title={onClick ? 'Click to view image' : undefined}
           style={{display: 'block', ...imgStyle}}/>
      {hasMark &&
         <Box sx={{
            position: 'absolute',
            left: `${clampPercent(issue.markX!)}%`,
            top: `${clampPercent(issue.markY!)}%`,
            width: markSize,
            height: markSize,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            border: '2px solid #FF5376',
            boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
            pointerEvents: 'none',
         }}/>
      }
   </Box>;
};

// Read-only card for an issue photo. Styled distinctly (red border + "Issue image"
// badge) so it is never mistaken for a packshot / catalog image. Clicking the photo opens
// it full screen, mirroring the preview behaviour of the packshot Thumbnail cards.
const IssueImageCard = ({issue}: IssueImageCardProps) => {
   const [preview, setPreview] = useState<boolean>(false);
   const theme = useTheme();
   const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));

   const details = [
      issue.location ? locationLabel(issue.location) : undefined,
      issue.point ? `Point ${issue.point}` : undefined,
   ].filter(Boolean).join(' · ');

   return <Card sx={{width: 245, marginBottom: '15px', marginLeft: '7px', marginRight: '7px', border: '2px solid', borderColor: 'error.main'}}
                variant={"outlined"}>
      <CardContent sx={{padding: '4px 8px 0', display: 'flex', alignItems: 'center', minHeight: '28px'}}>
         <CardTypeLabel label={"Issue image"} color={"#d32f2f"}/>
      </CardContent>
      <CardContent sx={{textAlign: 'center'}}>
         <Box sx={{position: 'relative', display: 'inline-block'}}>
            <IssueImage issue={issue}
                        imgStyle={{width: ISSUE_IMAGE_SIZE, height: 'auto'}}
                        markSize={18}
                        onClick={() => setPreview(true)}/>
            <Chip label={"Issue image"} color={"error"} size={"small"}
                  sx={{position: 'absolute', top: 6, left: 6, fontWeight: 600, pointerEvents: 'none'}}/>
         </Box>
         {issue.issue &&
            <Typography variant={"body2"} sx={{marginTop: '8px'}}>{issue.issue}</Typography>}
         {details &&
            <Typography variant={"caption"} color={"textSecondary"} display={"block"}>{details}</Typography>}
         {issue.createdAt &&
            <Typography variant={"caption"} color={"textSecondary"} display={"block"}>
               {DateFormat.toPresent(issue.createdAt)}
            </Typography>}
      </CardContent>
      <Dialog open={preview} onClose={() => setPreview(false)} onClick={() => setPreview(false)}
              fullScreen={fullScreen} maxWidth={"lg"}>
         <Box sx={{
            backgroundColor: 'rgba(255,255,255,0.5)',
            borderRadius: '5px',
            padding: '5px 2px 0px 2px',
            cursor: 'pointer',
            position: 'absolute',
            right: '5px',
            top: '5px'
         }}>
            <CloseIcon iconcolor={'#FF5376'}/>
         </Box>
         <IssueImage issue={issue}
                     imgStyle={{maxWidth: '100%', maxHeight: fullScreen ? '100vh' : '85vh', width: 'auto', height: 'auto'}}
                     markSize={30}/>
      </Dialog>
   </Card>;
};

const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

const locationLabel = (location: EnumItemIssueLocation) => {
   switch (location) {
      case EnumItemIssueLocation.FRONT:
         return 'Front';
      case EnumItemIssueLocation.BACK:
         return 'Back';
      case EnumItemIssueLocation.INSIDE:
         return 'Inside';
      default:
         return location;
   }
};
